/**
 * RealtimeFlowPreview - Live phone preview that updates as you edit
 *
 * This component renders a WhatsApp-style phone mockup that shows
 * the current screen in real-time as the user makes changes.
 */

import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  PhoneIphone as PhoneIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { flowComponentRegistry } from './flow-components';

const RealtimeFlowPreview = ({ screens, activeScreen, flowName }) => {
  // Find the current screen to preview
  const currentScreen = useMemo(() => {
    return screens.find(s => s.id === activeScreen) || screens[0];
  }, [screens, activeScreen]);

  // 1-based position of the active screen among all screens, for the header
  // indicator. Falls back to the first screen (matching currentScreen above).
  const screenIndex = useMemo(() => {
    const idx = screens.findIndex(s => s.id === activeScreen);
    return (idx === -1 ? 0 : idx) + 1;
  }, [screens, activeScreen]);

  // Render a component using the registry
  const renderComponent = (component) => {
    const flowComponent = flowComponentRegistry.get(component.type);

    if (!flowComponent) {
      return (
        <Box
          key={component.id}
          sx={{
            p: 1,
            mb: 1,
            bgcolor: '#f9fafb',
            borderRadius: 1,
            fontSize: '12px',
            color: '#6b7280',
          }}
        >
          Unknown: {component.type}
        </Box>
      );
    }

    // Use the registry's renderPreview method
    return (
      <Box
        key={component.id}
        sx={{
          mb: 1.5,
          '& .MuiTypography-root': {
            color: '#1f2937',
          },
          '& .MuiTextField-root': {
            '& .MuiOutlinedInput-root': {
              bgcolor: '#ffffff',
              color: '#1f2937',
              '& fieldset': {
                borderColor: '#d1d5db',
              },
              '&:hover fieldset': {
                borderColor: '#00a884',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#6b7280',
            },
          },
          '& .MuiFormControl-root': {
            '& .MuiFormLabel-root': {
              color: '#6b7280',
            },
            '& .MuiRadio-root, & .MuiCheckbox-root': {
              color: '#6b7280',
              '&.Mui-checked': {
                color: '#00a884',
              },
            },
            '& .MuiFormControlLabel-label': {
              color: '#1f2937',
              fontSize: '14px',
            },
          },
          '& .MuiSelect-root': {
            bgcolor: '#ffffff',
            color: '#1f2937',
          },
          '& .MuiButton-contained': {
            bgcolor: '#00a884',
            '&:hover': {
              bgcolor: '#00926f',
            },
          },
        }}
      >
        {flowComponent.renderPreview(component.properties || {}, { component })}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f0f2f5',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid #333',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PhoneIcon sx={{ color: '#00a884', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ color: '#e9edef', fontWeight: 500 }}>
          Live Preview
        </Typography>
        {screens.length > 0 && (
          <Chip
            label={`Screen ${screenIndex} of ${screens.length}`}
            size="small"
            sx={{
              ml: 'auto',
              height: 20,
              fontSize: '10px',
              bgcolor: 'rgba(233, 237, 239, 0.1)',
              color: '#e9edef',
            }}
          />
        )}
        <Chip
          label="Auto-updates"
          size="small"
          sx={{
            ml: screens.length > 0 ? 0 : 'auto',
            height: 20,
            fontSize: '10px',
            bgcolor: 'rgba(0, 168, 132, 0.2)',
            color: '#00a884',
          }}
        />
      </Box>

      {/* Phone Container, single scroll owner is the inner content area below */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
          overflow: 'hidden',
        }}
      >
        {/* Phone Frame */}
        <Box
          sx={{
            width: 280,
            minHeight: 500,
            maxHeight: 580,
            bgcolor: '#000',
            borderRadius: '28px',
            p: '6px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            flexShrink: 0,
          }}
        >
          {/* Phone Screen */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 488,
              maxHeight: 568,
              bgcolor: '#ffffff',
              borderRadius: '22px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* WhatsApp Header */}
            <Box
              sx={{
                bgcolor: '#008069',
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <BackIcon sx={{ color: '#ffffff', fontSize: 20, mr: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {flowName || 'WhatsApp Flow'}
              </Typography>
            </Box>

            {/* Screen Title */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #e0e0e0',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#1f2937',
                  fontWeight: 600,
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                {currentScreen?.title || 'Screen'}
              </Typography>
            </Box>

            {/* Flow Content */}
            <Box
              sx={{
                flex: 1,
                p: 1.5,
                overflow: 'auto',
                bgcolor: '#ffffff',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: '#d1d5db',
                  borderRadius: '2px',
                },
              }}
            >
              {currentScreen?.components?.length > 0 ? (
                currentScreen.components.map(renderComponent)
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#6b7280',
                    textAlign: 'center',
                    p: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1, color: '#6b7280' }}>
                    No components yet
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    Drag components from the palette to see them here
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Preview Footer */}
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                bgcolor: '#f5f5f5',
                borderTop: '1px solid #e0e0e0',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#6b7280', fontSize: '9px' }}
              >
                Managed by business. Learn more
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Info Footer */}
      <Box
        sx={{
          p: 1,
          borderTop: '1px solid #333',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
          Changes appear instantly as you edit
        </Typography>
      </Box>
    </Box>
  );
};

export default RealtimeFlowPreview;
